import type {
  AgeBand,
  CapabilityLevel,
  HilloNumber,
  LearnerGrade,
  Procedure,
  ProcedureDef,
  ProcedureInput,
  Side,
} from '@iculogbook/shared';
import { procedureByKey } from '@iculogbook/shared';

/**
 * The state behind the procedure quick-add form, and the pure functions that
 * move it around. No React and no Dexie here, so the rules that matter can be
 * tested directly.
 *
 * Two of those rules earn their own functions:
 *
 * - Changing the selected procedure must drop anything that no longer applies.
 *   A side left over from an arterial line would otherwise ride along onto a
 *   bronchoscopy, where the schema rejects it and the user sees an error about
 *   a field they cannot see.
 * - Building the record omits every optional field the user left alone, rather
 *   than storing an empty string. Absent means "not recorded"; "" would mean
 *   "recorded as nothing", and the PDF would have to render it.
 */

export type ProcedureFormValues = {
  procedureKey: string;
  occurredAt: Date;
  /** Null until chosen. Required, so the form cannot save without it. */
  ageBand: AgeBand | null;
  capabilityLevel: CapabilityLevel | null;
  variant: string | null;
  side: Side | null;
  /** Only ever sent when the procedure supports it. */
  ultrasoundGuided: boolean;
  attempts: number;
  successful: boolean;
  complications: string;
  indication: string;
  note: string;
  hillos: readonly HilloNumber[];
  /** Who the trainee supervised doing this specific procedure. */
  teachingGrades: readonly LearnerGrade[];
};

/** DATA_MODEL.md: attempts defaults to 1, successful defaults to yes. */
export const DEFAULT_ATTEMPTS = 1;
export const MAX_ATTEMPTS = 10;

export function initialValues(procedureKey: string, occurredAt: Date): ProcedureFormValues {
  return {
    procedureKey,
    occurredAt,
    ageBand: null,
    capabilityLevel: null,
    variant: null,
    side: null,
    ultrasoundGuided: false,
    attempts: DEFAULT_ATTEMPTS,
    successful: true,
    complications: '',
    indication: '',
    note: '',
    hillos: [],
    teachingGrades: [],
  };
}

/**
 * Loads a stored procedure, or an unsaved copy of one, back into the form.
 *
 * Absent optional fields become the form's empty values — a record with no
 * complications and a form with an empty complications box are the same thing,
 * and `buildProcedureInput` turns it back into an absent field on the way out.
 */
export function procedureValuesFrom(
  record: Procedure | ProcedureInput,
  occurredAt = record.occurredAt,
): ProcedureFormValues {
  return {
    procedureKey: record.procedureKey,
    occurredAt,
    ageBand: record.ageBand,
    capabilityLevel: record.capabilityLevel,
    variant: record.variant ?? null,
    side: record.side ?? null,
    ultrasoundGuided: record.ultrasoundGuided ?? false,
    attempts: record.attempts ?? DEFAULT_ATTEMPTS,
    successful: record.successful ?? true,
    complications: record.complications ?? '',
    indication: record.indication ?? '',
    note: record.note ?? '',
    hillos: record.hillos ?? [],
    teachingGrades: record.teachingGrades ?? [],
  };
}

export function sideApplies(definition: ProcedureDef | undefined): boolean {
  return definition?.sideApplicable === true;
}

export function ultrasoundApplies(definition: ProcedureDef | undefined): boolean {
  return definition?.usGuidanceApplicable === true;
}

export function variantsFor(definition: ProcedureDef | undefined): readonly string[] {
  return definition?.variants ?? [];
}

/**
 * Switching to a different procedure, keeping whatever still makes sense.
 *
 * A variant survives only if the new procedure offers the same one — "Femoral"
 * means the same thing on a CVC as on an arterial line, and re-tapping it would
 * be friction for no gain. Everything the new procedure does not support is
 * cleared.
 */
export function applyProcedureChange(
  values: ProcedureFormValues,
  procedureKey: string,
): ProcedureFormValues {
  const definition = procedureByKey(procedureKey);
  const variants = variantsFor(definition);

  return {
    ...values,
    procedureKey,
    variant: values.variant !== null && variants.includes(values.variant) ? values.variant : null,
    side: sideApplies(definition) ? values.side : null,
    ultrasoundGuided: ultrasoundApplies(definition) ? values.ultrasoundGuided : false,
  };
}

export type MissingField = 'ageBand' | 'capabilityLevel';

/** The two choices with no safe default. Everything else is optional. */
export function missingRequired(values: ProcedureFormValues): MissingField[] {
  const missing: MissingField[] = [];
  if (values.ageBand === null) missing.push('ageBand');
  if (values.capabilityLevel === null) missing.push('capabilityLevel');
  return missing;
}

function trimmed(value: string): string | undefined {
  const text = value.trim();
  return text === '' ? undefined : text;
}

/**
 * Turns the form state into the record the repository writes, or null if a
 * required choice is still missing.
 *
 * The result is still handed to `procedureInputSchema` before anything is
 * written. This function decides what to include; the shared schema decides
 * whether it is valid, and it is the only thing that does.
 */
export function buildProcedureInput(values: ProcedureFormValues): ProcedureInput | null {
  if (values.ageBand === null || values.capabilityLevel === null) return null;

  const definition = procedureByKey(values.procedureKey);
  const complications = trimmed(values.complications);
  const indication = trimmed(values.indication);
  const note = trimmed(values.note);

  return {
    procedureKey: values.procedureKey,
    occurredAt: values.occurredAt,
    ageBand: values.ageBand,
    capabilityLevel: values.capabilityLevel,
    attempts: values.attempts,
    successful: values.successful,
    ...(values.variant !== null && variantsFor(definition).includes(values.variant)
      ? { variant: values.variant }
      : {}),
    ...(values.side !== null && sideApplies(definition) ? { side: values.side } : {}),
    ...(ultrasoundApplies(definition) ? { ultrasoundGuided: values.ultrasoundGuided } : {}),
    ...(complications === undefined ? {} : { complications }),
    ...(indication === undefined ? {} : { indication }),
    ...(note === undefined ? {} : { note }),
    ...(values.hillos.length === 0 ? {} : { hillos: [...values.hillos] }),
    ...(values.teachingGrades.length === 0
      ? {}
      : { teachingGrades: [...values.teachingGrades] }),
  };
}
